import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ExpirationBadgeProps {
  expiresAt?: string;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ExpirationBadge({ expiresAt }: ExpirationBadgeProps) {
  if (!expiresAt) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        No expiration
      </Badge>
    );
  }

  const now = new Date();
  const expires = new Date(expiresAt);
  const isExpired = expires < now;

  if (isExpired) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive">Expired</Badge>
        </TooltipTrigger>
        <TooltipContent>Expired on {formatDate(expiresAt)}</TooltipContent>
      </Tooltip>
    );
  }

  const daysRemaining = Math.ceil(
    (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysRemaining <= 7) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="warning">
            Expires in {daysRemaining} day{daysRemaining !== 1 ? "s" : ""}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Expires on {formatDate(expiresAt)}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline">Expires {formatDate(expiresAt)}</Badge>
      </TooltipTrigger>
      <TooltipContent>
        {daysRemaining} days remaining until expiration
      </TooltipContent>
    </Tooltip>
  );
}
